import { useTranslations } from 'next-intl';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_REFERENCE_PROMPTS } from '@/app/constants';
import {
  getReferenceDeleteConfirmationCountKey,
  resolveReferenceVideoDuration,
  useGlobalModal,
  useReferenceDeleteConfirmation,
} from '@/app/hooks';
import type { AddReferenceAndMentionsOptions } from '@/app/hooks/use-mentionable-prompts';
import type { ElementModality, ElementType, ImagePrompt, ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import {
  CREATE_FORM_INPUT_ERROR_KEY_MAP,
  getReferenceFormInputValidationError,
  reportEvent,
  validateReferenceVideoDurationCombination,
} from '@/app/utils';
import type { ElementsDialogState } from '../_components/ElementsDialog';

type ElementReferencePrompt = Extract<ReferencePrompt, { type: 'element' }>;

/**
 * 主体/元素管理 Hook 的配置项
 */
interface UseElementsManagerConfig {
  /** 任务类型，用于存储键和事件上报 */
  taskType: 'character2video' | 'reference2image' | 'storyGrid';
  /** 当前模型版本。传了才会校验视频主体的时长组合（「1 个 5-8s 或 2 个 5s 内」）。 */
  modelVersion?: string;
  /** 当前的参考提示列表 */
  referencePrompts: ReferencePrompt[];
  /** 添加参考和提及的函数 */
  addReferenceAndMentions: (reference: ReferencePrompt, options?: AddReferenceAndMentionsOptions) => boolean;
  /** 元素选择时按任务补充额外 reference / mention 节点 */
  getElementReferenceExtras?: (args: {
    element: ElementType;
    reference: ElementReferencePrompt;
    referencePrompts: ReferencePrompt[];
  }) => AddReferenceAndMentionsOptions | undefined | Promise<AddReferenceAndMentionsOptions | undefined>;
  /** 移除参考和提及的函数 */
  removeReferenceAndMentions: (id: string, showConfirm?: boolean) => void;
  /** 更新参考和提及的函数 */
  updateReferenceAndMentions: (id: string, updates: Partial<ReferencePrompt>) => void;
  /** 更新文本提示的函数 */
  updateTextPrompt: (content: TextPromptWithInputItem) => void;
  /** 验证表单的错误信息 */
  formValidationErrorMessage?: ReactNode;
  /** 重置最大参考数量错误提示 */
  resetMaxReferenceError: () => void;
}

/**
 * 主体/元素管理 Hook 的返回值
 */
interface UseElementsManagerReturn {
  /** 元素对话框状态 */
  elementsDialogState: ElementsDialogState;
  /** 设置元素对话框状态 */
  setElementsDialogState: (state: ElementsDialogState) => void;
  /** 验证错误信息 */
  validationErrorMessage: ReactNode | string;
  /** 打开元素选择器 */
  toggleElementsSelector: (allowedModalities?: Array<ElementModality>) => void;
  /** 打开元素创建对话框 */
  handleOpenElementCreateDialog: (initialImagePrompt: ImagePrompt, imageId: string) => void;
  /** 打开元素查看对话框 */
  handleOpenElementViewDialog: (element: ElementType, editMode?: boolean) => void;
  /** 处理元素选择。视频主体需要先异步探测时长再决定能否入列，因此返回 Promise。 */
  handleElementsSelect: (elements: ElementType[]) => Promise<void>;
  /** 处理元素保存 */
  handleSaveElement: (element: ElementType, id?: string | null) => void;
  /** 计算总图片数量 */
  calculateTotalImageCount: () => number;
  /** 删除提及前的处理 */
  handleBeforeDeleteMention: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  /** 显示最大参考错误 */
  handleShowMaxReferenceError: (maxCount?: number) => void;
  /** 关闭确认对话框 */
  closeConfirmDialog: () => void;
}

/**
 * 主体/元素管理 Hook
 *
 * 这个 Hook 封装了与主体(element)和参考图片(reference)相关的通用逻辑，包括：
 * - 元素对话框状态管理
 * - 元素的增删改查操作
 * - 表单验证逻辑
 * - 删除确认逻辑
 * - 图片数量计算等
 *
 * @param config 配置参数
 * @returns 管理器方法和状态
 */
export const useElementsManager = (config: UseElementsManagerConfig): UseElementsManagerReturn => {
  const {
    taskType,
    modelVersion,
    referencePrompts,
    addReferenceAndMentions,
    getElementReferenceExtras,
    removeReferenceAndMentions,
    updateReferenceAndMentions,
    updateTextPrompt,
    resetMaxReferenceError,
    formValidationErrorMessage,
  } = config;

  const t = useTranslations();
  const { open: openConfirmDialog, close: closeConfirmDialog } = useGlobalModal('confirmDialog');

  // 元素对话框状态管理
  const [elementsDialogState, setElementsDialogState] = useState<ElementsDialogState>({ type: 'none' });

  /**
   * 表单验证逻辑
   * 检查参考提示是否为空、是否有禁用项、是否有上传中的项等
   */
  const validationErrorMessage = useMemo(() => {
    const inputValidationError = getReferenceFormInputValidationError(referencePrompts);
    if (inputValidationError) {
      return t(CREATE_FORM_INPUT_ERROR_KEY_MAP[inputValidationError]);
    }

    // 返回通用的表单验证错误信息
    return formValidationErrorMessage;
  }, [referencePrompts, formValidationErrorMessage, t]);

  /**
   * 打开/关闭元素选择器
   * 如果选择器已打开，则关闭；否则打开并上报相关事件
   * @param allowedModalities 允许选择的 modality 类型，如果不传则允许选择所有类型
   */
  const toggleElementsSelector = useCallback((allowedModalities?: Array<ElementModality>) => {
    let open = true;
    setElementsDialogState((prev) => {
      if (prev.type === 'elementsSelector' && prev.allowedModalities === allowedModalities) {
        open = false;
      }
      return open
        ? {
            type: 'elementsSelector',
            allowedModalities,
          }
        : {
            type: 'none',
          };
    });

    open &&
      reportEvent('my_element_click', {
        click_position: 'character_page', // 保持现有的上报格式
      });
  }, []);

  /**
   * 打开元素创建对话框
   * @param initialImagePrompt 初始图片提示
   * @param imageId 图片ID
   */
  const handleOpenElementCreateDialog = useCallback((initialImagePrompt: ImagePrompt, imageId: string) => {
    setElementsDialogState({
      type: 'subjectCreate',
      initialImagePrompt,
      imageId,
    });
    reportEvent('create_element_page_view', {
      refer_element: 'image_change',
    });
  }, []);

  /**
   * 打开元素查看/编辑对话框
   * @param element 要查看/编辑的元素
   * @param editMode 是否为编辑模式，默认为查看模式
   */
  const handleOpenElementViewDialog = useCallback((element: ElementType, editMode = false) => {
    setElementsDialogState({
      type: editMode ? 'elementEdit' : 'elementView',
      element,
    });
  }, []);

  /**
   * 处理元素选择
   * 将选中的元素添加到参考列表中，跳过已存在的元素
   * @param elements 选中的元素列表
   */
  const handleElementsSelect = useCallback(
    async (elements: ElementType[]) => {
      const newElements = elements.filter(
        (element) => !!element.id && !referencePrompts.some((ref) => ref.id === element.id),
      );

      // 视频主体要先校验时长组合再入列：不能假设主体一定 <= 5s（官方预置主体由后端配置，
      // 不经过前端上传校验），否则「2 个 5s 内」的规则会被绕过。
      const videoElements = modelVersion ? newElements.filter((element) => element.modality === 'video') : [];
      if (videoElements.length > 0) {
        const durations = await Promise.all(
          videoElements.map((element) =>
            resolveReferenceVideoDuration(
              element.components?.find((component) => component.content_type === 'video')?.content,
            ),
          ),
        );
        const validation = validateReferenceVideoDurationCombination(modelVersion, referencePrompts, durations);
        if (!validation.ok) {
          toast({ type: 'warning', message: t('create.createForm.upload.videoDurationLimit') });
          return;
        }
      }

      let nextReferencePrompts = referencePrompts;
      for (const element of newElements) {
        // 创建新的参考对象
        const newReference: ReferencePrompt = {
          id: element.id as string,
          type: 'element',
          name: element.name || '',
          status: 'done',
          prompt: element,
        };

        // 添加到参考列表
        const maybeOptions = getElementReferenceExtras?.({
          element,
          reference: newReference,
          referencePrompts: nextReferencePrompts,
        });
        const options = maybeOptions instanceof Promise ? await maybeOptions : maybeOptions;
        const added = addReferenceAndMentions(newReference, options);
        if (added) {
          nextReferencePrompts = [...nextReferencePrompts, newReference, ...(options?.extraReferences ?? [])];
        }
      }
    },
    [referencePrompts, addReferenceAndMentions, getElementReferenceExtras, modelVersion, t],
  );

  /**
   * 处理元素保存
   * 用于更新已存在的元素或保存新创建的元素
   * @param element 要保存的元素
   * @param id 元素ID（可选）
   */
  const handleSaveElement = useCallback(
    (element: ElementType, id?: string | null) => {
      if (id) {
        // 查找是否在参考列表中存在对应的项
        const index = referencePrompts.findIndex((prompt) => prompt.id === id);
        if (index !== -1) {
          // 创建新的参考对象
          const newReference: ReferencePrompt = {
            type: 'element',
            id: element.id || '',
            name: element.name || '',
            status: 'done',
            prompt: element,
          };

          // 更新参考列表和相关的 mentions
          updateReferenceAndMentions(id, newReference);
        }
      }
    },
    [referencePrompts, updateReferenceAndMentions],
  );

  /**
   * 计算所有参考图片的总数
   * 包括普通图片和主体内的图片数量
   * @returns 总图片数量
   */
  const calculateTotalImageCount = useCallback(() => {
    let totalImageCount = 0;

    referencePrompts.forEach(({ type, prompt }) => {
      if (type === 'image' && prompt) {
        // 普通图片计数
        totalImageCount += 1;
      } else if (type === 'element' && prompt && 'components' in prompt) {
        // 主体内的图片数量
        const componentImages = Array.isArray(prompt.components)
          ? prompt.components.filter((component: { content_type?: string }) => component.content_type === 'image')
              .length
          : 0;
        totalImageCount += componentImages;
      }
    });

    return totalImageCount;
  }, [referencePrompts]);

  /**
   * 删除提及前的处理逻辑
   * 包含确认对话框和删除逻辑
   * @param mentionsIdsToDelete 要删除的提及ID列表
   * @param updatedContent 更新后的内容
   * @returns 是否允许立即删除
   */
  const { handleBeforeDeleteMention } = useReferenceDeleteConfirmation({
    confirmationCountKey: getReferenceDeleteConfirmationCountKey(taskType),
    onConfirmDelete: (referenceIdsToDelete) => {
      referenceIdsToDelete.forEach((mentionId) => {
        removeReferenceAndMentions(mentionId);
      });
    },
    onShowConfirm: (referenceIdsToDelete, updatedContent) => {
      openConfirmDialog({
        content: t('create.char2VideoForm.dialog.deleteConfirm.content'),
        secondaryButtonText: t('create.char2VideoForm.dialog.deleteConfirm.closeButtonText'),
        primaryButtonText: t('create.char2VideoForm.dialog.deleteConfirm.confirmButtonText'),
        onPrimaryAction: () => {
          updateTextPrompt(updatedContent);
          referenceIdsToDelete.forEach((mentionId) => {
            removeReferenceAndMentions(mentionId);
          });
        },
      });
      return false;
    },
  });

  /**
   * 显示最大参考数量错误提示
   */
  const handleShowMaxReferenceError = useCallback(
    (maxCount = MAX_REFERENCE_PROMPTS) => {
      openConfirmDialog({
        content: t('create.char2VideoForm.dialog.maxReferenceError.content', {
          count: maxCount,
        }),
        primaryButtonText: t('create.char2VideoForm.dialog.maxReferenceError.confirmButtonText'),
        onPrimaryAction: resetMaxReferenceError,
        onSecondaryAction: resetMaxReferenceError,
        onClose: resetMaxReferenceError,
      });
    },
    [openConfirmDialog, resetMaxReferenceError, t],
  );

  return {
    elementsDialogState,
    setElementsDialogState,
    validationErrorMessage,
    toggleElementsSelector,
    handleOpenElementCreateDialog,
    handleOpenElementViewDialog,
    handleElementsSelect,
    handleSaveElement,
    calculateTotalImageCount,
    handleBeforeDeleteMention,
    handleShowMaxReferenceError,
    closeConfirmDialog,
  };
};
